/**
 * Element Schema Type Validators
 * Zod validators for element types and schemas
 */

import { z } from 'zod'

import type { ElementSchema, ElementType } from '@/types/element-schema.type'

/**
 * Zod schema for ElementSelectOption
 */
export const ElementSelectOptionSchema = z.object({
  label: z.string().min(1, 'Label is required'),
  value: z.union([z.string(), z.number()]),
})

/**
 * Zod schema for ElementType
 */
export const ElementTypeSchema = z.enum([
  'text',
  'textarea',
  'number',
  'boolean',
  'date',
  'color',
  'image',
  'video',
  'file',
  'select',
  'radio',
  'checkbox',
  'json',
  'array',
  'external',
  // Phase 1 additional types
  'layout',
  'background',
  'container',
  'slides',
])

/**
 * Zod schema for ElementSchema (recursive)
 */
export const ElementSchemaSchema: z.ZodType<ElementSchema> = z.lazy(() =>
  z.object({
    key: z.string().min(1, 'Key is required'),
    type: ElementTypeSchema,
    label: z.string().min(1, 'Label is required'),
    required: z.boolean().optional(),
    default: z.unknown().optional(),
    placeholder: z.string().optional(),
    helpText: z.string().optional(),
    options: z.array(ElementSelectOptionSchema).optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    minLength: z.number().int().min(0).optional(),
    maxLength: z.number().int().min(0).optional(),
    accept: z.array(z.string()).optional(),
    children: z.array(ElementSchemaSchema).optional(),
    validation: z.record(z.unknown()).optional(),
    readOnly: z.boolean().optional(),
    hidden: z.boolean().optional(),
    className: z.string().optional(),
  })
)

/**
 * Zod schema for SectionElementSchema
 */
export const SectionElementSchemaSchema = z.object({
  componentType: z.string().min(1, 'Component type is required'),
  label: z.string().min(1, 'Label is required'),
  description: z.string().optional(),
  elements: z.array(ElementSchemaSchema).min(1, 'At least one element is required'),
  layout: z
    .object({
      columns: z.number().int().min(1).max(12).optional(),
      rows: z.number().int().min(1).optional(),
      gap: z.string().optional(),
    })
    .optional(),
  icon: z.string().optional(),
  category: z.string().optional(),
  tags: z.array(z.string()).optional(),
})

/**
 * Create Zod validator for element type
 */
export function createTypeValidator(type: ElementType, schema: ElementSchema): z.ZodType {
  switch (type) {
    case 'text':
    case 'textarea':
    case 'color':
      return z.string()

    case 'number':
      return z.number()

    case 'boolean':
    case 'checkbox':
      return z.boolean()

    case 'date':
      return z.string().datetime().or(z.date())

    case 'image':
    case 'video':
    case 'file':
      return z.string().url().or(z.string())

    case 'select':
    case 'radio':
      if (schema.options && schema.options.length > 0) {
        const values = schema.options.map(opt => opt.value)
        if (values.every(v => typeof v === 'string')) {
          return z.enum(values as [string, ...string[]])
        }
        // For mixed string/number values, use unknown
        return z.unknown()
      }
      return z.unknown()

    case 'json':
      return z.record(z.unknown())

    case 'array':
      return z.array(z.unknown())

    case 'external':
      return z.unknown()

    // Phase 1 additional types - use record/object validation
    case 'layout':
    case 'background':
    case 'container':
    case 'slides':
      return z.record(z.unknown())

    default:
      return z.unknown()
  }
}
